/**
 * The arithmetic and ordering behind the leak report screen.
 *
 * Pulled out of the component for the same reason `table-game.ts` was: a function
 * that decides what order a player reads their own weaknesses in should be testable
 * without mounting React, and every bug in here is silent. Ordering that quietly
 * falls back to the wrong thing still renders a plausible-looking list.
 */

/**
 * One slice of the trainer report — a decision kind, a position, a stack bucket, a
 * tournament stage or a table size.
 *
 * `accuracy` and `evLostPer100` are null together, and only when `diagnosable` is
 * false. A thin segment is still returned with its count: "you have played the bubble
 * four times" is useful and "you are bad at the bubble" from four spots is not.
 *
 * Declared HERE rather than in api.ts so this module has no dependency on the fetch
 * layer — api.ts reads `import.meta.env`, which exists only under Vite, and these
 * functions have to be testable outside it. api.ts re-exports the type, so callers
 * still see one name.
 *
 * Mirrors `Segment` in app/src/lib/leaks.ts.
 */
export interface LeakSegment {
  axis: "kind" | "position" | "stack" | "stage" | "table" | string;
  key: string;
  label: string;
  spots: number;
  accuracy: number | null;
  /**
   * Avoidable loss per 100 spots, as a percentage of what was at stake at each node.
   *
   * NOT big blinds and not chips. A raw loss in the solve's own units is not
   * comparable between two nodes — the same figure is a catastrophe at one and a
   * rounding error at another — so the report measures cost as a share of the spread
   * of outcomes at the node it happened in. Label it accordingly wherever it renders.
   */
  evLostPer100: number | null;
  /** Spots here recorded before the comparable cost existed, so carrying none. */
  unmeasured: number;
  diagnosable: boolean;
}

/** Headings for each axis of the segmented report, in the player's language. */
export const AXIS_TITLE: Record<string, string> = {
  kind: "By decision",
  position: "By position",
  stack: "By stack depth",
  stage: "By tournament stage",
  table: "By table size",
};

/** The order the axes read in: broadest question first, narrowest last. */
export const AXIS_ORDER = ["kind", "stack", "stage", "position", "table"];

/**
 * The order segments read in WITHIN an axis, where the axis has a natural one.
 *
 * Sorting these by volume put "5–8bb" after "17–25bb", which is not a stack-depth
 * ordering and made the column impossible to scan. Where an axis has an inherent
 * sequence, that sequence is the ordering; where it does not — decision type, table
 * size — volume is the sensible fallback and stays.
 */
export const KEY_ORDER: Record<string, string[]> = {
  stage: ["chip_ev", "prebubble", "bubble", "ladder"],
  position: ["UTG", "UTG1", "UTG2", "MP", "MP1", "HJ", "LJ", "CO", "BTN", "SB", "BB"],
};

/**
 * Stack buckets sort on the number they start with, not on a list of literals.
 *
 * The literal list was written as "5–8bb" with an EN-DASH and matched nothing the
 * moment anything produced a plain hyphen — and the failure is silent, because an
 * unmatched key falls back to volume order, which looks like a deliberate ordering
 * rather than a broken one. A bucket label always leads with its lower bound, so
 * read that and let the dash be whatever it is.
 */
export function stackRank(key: string): number {
  const n = parseInt(key, 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

/** Natural order where the axis has one, most-played first where it does not. */
export function orderSegments(axis: string, segs: readonly LeakSegment[]): LeakSegment[] {
  if (axis === "stack") {
    return [...segs].sort((a, b) => stackRank(a.key) - stackRank(b.key));
  }
  const order = KEY_ORDER[axis];
  if (!order) return [...segs].sort((a, b) => b.spots - a.spots);
  const rank = (k: string) => {
    // An unrecognised key sorts to the END, not to the front where indexOf's -1 would
    // put it. A position label we have not seen must not silently lead the list.
    const i = order.indexOf(k);
    return i < 0 ? order.length : i;
  };
  return [...segs].sort((a, b) => rank(a.key) - rank(b.key) || b.spots - a.spots);
}

/** Axes present in the data, in reading order, with anything unknown appended. */
export function axesIn(segments: readonly LeakSegment[]): string[] {
  const present = new Set(segments.map((s) => s.axis));
  return AXIS_ORDER.filter((a) => present.has(a))
    .concat([...present].filter((a) => !AXIS_ORDER.includes(a)).sort());
}

export function groupByAxis(segments: readonly LeakSegment[]): Map<string, LeakSegment[]> {
  const byAxis = new Map<string, LeakSegment[]>();
  for (const s of segments) {
    const list = byAxis.get(s.axis) ?? [];
    list.push(s);
    byAxis.set(s.axis, list);
  }
  return byAxis;
}

/* ------------------------------------------------------------------ units --- */

/**
 * TWO percentage helpers, because there are two scales on this screen and one helper
 * would be silently wrong for one of them.
 *
 * Trainer accuracies arrive as 0–1 fractions. Table statistics come out of the
 * engine's `derive()` already on a 0–100 scale. Multiplying the second by a hundred
 * produced "2740%" the first time these shared a function.
 */
export const pct = (v: number | null) => (v === null ? "—" : `${(v * 100).toFixed(0)}%`);
export const pctScaled = (v: number | null) => (v === null ? "—" : `${v.toFixed(0)}%`);

/**
 * Avoidable loss, in the unit it is actually measured in.
 *
 * A share of what was at stake at the node — not chips, not big blinds, and not
 * "equity", which would name a quantity the solve does not report. The raw loss is in
 * the solve's own units and is not comparable between two nodes, so the report
 * compares this instead; the label has to say so or the number invites the wrong
 * arithmetic.
 */
export const fmtEv = (v: number | null) =>
  (v === null ? "—" : `${v.toFixed(1)}% of what was at stake`);

/** The same number where space is tight and the card has already explained it. */
export const fmtEvShort = (v: number | null) => (v === null ? "—" : `${v.toFixed(1)}%`);

/** Mirrors STRONG_ACCURACY in app/src/lib/leaks.ts; used for copy only. */
export const STRONG = 0.9;
